import React, { useEffect, useRef, useState } from "react";

import ReCAPTCHA from "react-google-recaptcha";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import HeaderOne from "../components/header/HeaderOne";
import FooterOne from "../components/footer/FooterOne";
import Breadcrumb from "./Breadcrumb";
import MapOne from "../components/map/MapOne";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000"
).replace(/\/+$/, "");

const INITIAL_FORM = {
  name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

function ContactUs() {
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(true);

  const [form, setForm] = useState(INITIAL_FORM);

  const [formLoading, setFormLoading] = useState(false);

  const [formStatus, setFormStatus] = useState({
    type: "",
    message: "",
  });

  const recaptchaRef = useRef(null);

  const [recaptchaToken, setRecaptchaToken] = useState("");

  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE;

  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [contractLoading, setContractLoading] = useState(false);
  const [contractError, setContractError] = useState("");
  const [contractData, setContractData] = useState({ title: "", editor: "" });

  const breadcrumbs = [
    {
      label: "Ana Sayfa",
      link: "/",
    },
    {
      label: "İletişim",
    },
  ];

  /*
   * İletişim kartları ve haritalar için
   * veritabanındaki tüm iletişim kayıtlarını çeker.
   */
  useEffect(() => {
    const controller = new AbortController();

    const fetchContacts = async () => {
      try {
        setContactsLoading(true);

        const searchParams = new URLSearchParams(window.location.search);

        const currentLang = searchParams.get("lang") || "tr";

        const response = await fetch(
          `${API_BASE_URL}/api/contact/contact-info?lang=${encodeURIComponent(
            currentLang,
          )}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          },
        );

        const responseData = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(
            responseData?.message ||
              `İletişim bilgileri alınamadı: ${response.status}`,
          );
        }

        /*
         * Yeni route dizi döndürüyor.
         * Eski route tek nesne döndürürse de çalışır.
         */
        const normalizedContacts = Array.isArray(responseData)
          ? responseData
          : responseData
            ? [responseData]
            : [];

        setContacts(normalizedContacts);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("CONTACT_INFO_ERROR:", error);

          setContacts([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setContactsLoading(false);
        }
      }
    };

    fetchContacts();

    return () => {
      controller.abort();
    };
  }, []);

  const formatContactPhone = (value) => {
    let digits = String(value || "").replace(/\D/g, "");

    // Kullanıcı 90 ile yapıştırırsa kaldır
    if (digits.startsWith("90")) {
      digits = digits.slice(2);
    }

    // Kullanıcı 0 ile başlarsa kaldır
    if (digits.startsWith("0")) {
      digits = digits.slice(1);
    }

    digits = digits.slice(0, 10);

    const firstPart = digits.slice(0, 3);
    const secondPart = digits.slice(3, 6);
    const thirdPart = digits.slice(6, 8);
    const fourthPart = digits.slice(8, 10);

    return [firstPart, secondPart, thirdPart, fourthPart]
      .filter(Boolean)
      .join(" ");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    let nextValue = value;

    if (name === "name") {
      nextValue = value.replace(/[0-9]/g, "");
    }

    setForm((previousForm) => ({
      ...previousForm,
      [name]: nextValue,
    }));

    if (formStatus.type === "error") {
      setFormStatus({
        type: "",
        message: "",
      });
    }
  };
  const handlePhoneChange = (value) => {
    setForm((previousForm) => ({
      ...previousForm,
      phone: value || "",
    }));

    if (formStatus.type === "error") {
      setFormStatus({
        type: "",
        message: "",
      });
    }
  };

  const handleCaptchaChange = (token) => {
    setRecaptchaToken(token || "");

    if (token && formStatus.type === "error") {
      setFormStatus({
        type: "",
        message: "",
      });
    }
  };

  const handleCaptchaExpired = () => {
    setRecaptchaToken("");

    setFormStatus({
      type: "error",
      message: "CAPTCHA doğrulamasının süresi doldu. Lütfen tekrar doğrulayın.",
    });
  };

  const handleCaptchaError = () => {
    setRecaptchaToken("");

    setFormStatus({
      type: "error",
      message: "CAPTCHA yüklenirken bir hata oluştu. Lütfen tekrar deneyin.",
    });
  };

  const handleCaptchaRefresh = () => {
    recaptchaRef.current?.reset();

    setRecaptchaToken("");

    if (formStatus.type === "error") {
      setFormStatus({
        type: "",
        message: "",
      });
    }
  };

  const openContractModal = async () => {
    setContractModalOpen(true);
    setContractLoading(true);
    setContractError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/contract/394`);

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.message || data?.msg || "Aydınlatma metni alınamadı.");
      }

      setContractData({
        title: data?.title || "Aydınlatma Metni",
        editor: data?.editor || "",
      });
    } catch (error) {
      console.error("CONTRACT_DETAIL_ERROR:", error);
      setContractError("Aydınlatma metni yüklenirken bir hata oluştu.");
    } finally {
      setContractLoading(false);
    }
  };

  const closeContractModal = () => {
    setContractModalOpen(false);
  };

  useEffect(() => {
    if (!contractModalOpen) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeContractModal();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [contractModalOpen]);
  const validateForm = () => {
    const name = form.name.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();
    const subject = form.subject.trim();
    const message = form.message.trim();

    if (!name) {
      return "Lütfen adınızı ve soyadınızı yazın.";
    }

    if (name.length < 2) {
      return "Ad soyad alanı en az 2 karakter olmalıdır.";
    }

    if (/[0-9]/.test(name)) {
      return "Ad soyad alanına rakam girilemez.";
    }

    const namePattern = /^[A-Za-zÇĞİÖŞÜçğıöşü' -]+$/;

    if (!namePattern.test(name)) {
      return "Ad soyad alanında yalnızca harf kullanılabilir.";
    }

    if (!email) {
      return "Lütfen e-posta adresinizi yazın.";
    }

    if (!email.includes("@")) {
      return "E-posta adresinde @ karakteri bulunmalıdır.";
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailPattern.test(email)) {
      return "Lütfen geçerli bir e-posta adresi yazın.";
    }

    if (!phone) {
      return "Lütfen telefon numaranızı yazın.";
    }

    const phoneDigits = phone.replace(/\D/g, "");

    if (phoneDigits.length < 7 || phoneDigits.length > 15) {
      return "Lütfen geçerli bir telefon numarası yazın.";
    }
    if (!subject) {
      return "Lütfen mesaj konusunu yazın.";
    }

    if (!message) {
      return "Lütfen mesajınızı yazın.";
    }

    if (message.length < 10) {
      return "Mesajınız en az 10 karakter olmalıdır.";
    }

    if (!recaptchaSiteKey) {
      return "CAPTCHA yapılandırması bulunamadı.";
    }

    if (!recaptchaToken) {
      return 'Lütfen "Ben robot değilim" doğrulamasını tamamlayın.';
    }

    if (!isConsentChecked) {
      return "Lütfen kişisel verilerin işlenmesine ilişkin aydınlatma metnini okuyup onaylayınız.";
    }

    return "";
  };

  /*
   * İletişim formunu mail endpointine gönderir.
   */
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (formLoading) {
      return;
    }

    const validationError = validateForm();

    if (validationError) {
      setFormStatus({
        type: "error",
        message: validationError,
      });

      return;
    }

    try {
      setFormLoading(true);

      setFormStatus({
        type: "",
        message: "",
      });

      /*
       * Backend ayrı bir subject alanı okumuyor.
       * Bu nedenle konu mesajın başına ekleniyor.
       */
      const currentLang =
        new URLSearchParams(window.location.search).get("lang") || "tr";

      const response = await fetch(`${API_BASE_URL}/api/contact/send-message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone ? `+${form.phone.trim()}` : "",
          subject: form.subject.trim(),
          message: form.message.trim(),
          lang: currentLang,
          recaptchaToken: recaptchaToken,
        }),
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok || responseData.success === false) {
        throw new Error(responseData.message || "Mesaj gönderilemedi.");
      }

      setForm(INITIAL_FORM);

      setRecaptchaToken("");
      setIsConsentChecked(false);

      recaptchaRef.current?.reset();

      setFormStatus({
        type: "success",
        message: responseData.message || "Mesajınız başarıyla gönderildi.",
      });
    } catch (error) {
      console.error("CONTACT_FORM_ERROR:", error);

      setRecaptchaToken("");

      recaptchaRef.current?.reset();

      setFormStatus({
        type: "error",
        message: error.message || "Mesaj gönderilirken bir hata oluştu.",
      });
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div>
      <style>
        {`
          /*
           * Tema başlıklarının arkasından gelen
           * büyük dekoratif yazıları kaldırır.
           */
          .demirsan-clean-title {
            position: relative;

            background: none !important;
            background-image: none !important;
          }

          .demirsan-clean-title::before,
          .demirsan-clean-title::after,
          .demirsan-clean-title
            .pre-title::before,
          .demirsan-clean-title
            .pre-title::after,
          .demirsan-clean-title
            .title::before,
          .demirsan-clean-title
            .title::after,
          .demirsan-clean-title
            span::before,
          .demirsan-clean-title
            span::after,
          .demirsan-clean-title
            p::before,
          .demirsan-clean-title
            p::after,
          .demirsan-clean-title
            h2::before,
          .demirsan-clean-title
            h2::after,
          .demirsan-clean-title
            h3::before,
          .demirsan-clean-title
            h3::after {
            display: none !important;

            visibility: hidden !important;
            opacity: 0 !important;

            content: none !important;

            background: none !important;
            background-image: none !important;
          }

          .demirsan-clean-title
            .pre-title {
            position: relative;
            z-index: 2;

            margin-bottom: 8px;
            padding: 0 !important;
font-size: 18px !important;
            color: #e60000 !important;

            background: none !important;
            background-image: none !important;
          }

          .demirsan-clean-title
            .title {
            position: relative;
            z-index: 2;

            margin-top: 0;

            color: #0b1735 !important;

            background: none !important;
            background-image: none !important;
          }

          /*
           * İletişim formu mesajları
           */
          .demirsan-contact-form-status {
            width: 100%;

            margin-bottom: 24px;
            padding: 14px 18px;

            border: 1px solid transparent;
            border-radius: 6px;

            font-size: 15px;
            line-height: 1.5;
          }

          .demirsan-contact-form-status.success {
            color: #276738;
            background: #edf9f0;
            border-color: #cbe8d2;
          }

          .demirsan-contact-form-status.error {
            color: #a20c18;
            background: #fff0f1;
            border-color: #f1c8cc;
          }

          /*
           * Form butonu gönderim sırasında
           */
          #contact-form
            button:disabled {
            cursor: not-allowed;
            opacity: 0.7;
          }

          /*
           * Telefon alanındaki tarayıcı oklarını
           * ve tema uyumsuzluklarını engellemez;
           * mevcut form görünümünü korur.
           */
          #contact-form input,
          #contact-form textarea {
            width: 100%;
          }

          #contact-form textarea {
            resize: vertical;
          }

          @media (max-width: 767px) {
            .demirsan-contact-form-status {
              padding: 12px 14px;
              font-size: 14px;
            }
          }
            #contact-form .name-email {
  display: grid !important;
  grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  gap: 20px !important;
  width: 100%;
  margin-bottom: 20px;
}

#contact-form .name-email > input {
  width: 100% !important;
  min-width: 0;
  margin: 0 !important;
}

#contact-form > textarea {
  display: block;
  width: 100% !important;
  margin: 0 0 20px !important;
}

#contact-form input,
#contact-form textarea {
  box-sizing: border-box;
}

@media (max-width: 767px) {
  #contact-form .name-email {
    grid-template-columns: 1fr !important;
    gap: 15px !important;
    margin-bottom: 15px;
  }

  #contact-form > textarea {
    margin-bottom: 15px !important;
  }
    #contact-form .demirsan-contact-phone-dropdown {
  width: min(340px, calc(100vw - 50px)) !important;
}
}
  .rts-map-area::after {
  display: none !important;
  content: none !important;
  background: none !important;
  background-image: none !important;
}
  /* =============================================
   FORM INPUTLARI - ÜRÜNLERDEKİ TASARIM
============================================= */

#contact-form input,
#contact-form textarea {
  width: 100% !important;

  margin: 0 !important;
  padding: 14px 16px !important;

  box-sizing: border-box !important;

  color: #17213a !important;
  background: #f6f7f9 !important;

  border: 1px solid transparent !important;
  border-radius: 9px !important;

  outline: none !important;

  font-family: inherit !important;
  font-size: 14px !important;
  line-height: 1.5 !important;

  transition:
    border-color .2s ease,
    box-shadow .2s ease,
    background-color .2s ease !important;
}

#contact-form input {
  height: 52px !important;
}

#contact-form textarea {
  min-height: 135px;
  resize: vertical;
}

#contact-form input:focus,
#contact-form textarea:focus {
  background: #ffffff !important;

  border-color: rgba(226, 6, 20, .5) !important;

  box-shadow:
    0 0 0 4px rgba(226, 6, 20, .08) !important;
}


/* =============================================
   TELEFON
============================================= */

#contact-form .demirsan-contact-phone {
  width: 100%;
  min-width: 0;
}

#contact-form .demirsan-contact-phone-container {
  position: relative !important;
  width: 100% !important;
}


/* Telefon inputu */

#contact-form .demirsan-contact-phone-input {
  width: 100% !important;
  height: 52px !important;

  margin: 0 !important;

  /* bayrak için solda boşluk */
  padding: 14px 16px 14px 58px !important;

  box-sizing: border-box !important;

  color: #17213a !important;
  background: #f6f7f9 !important;

  border: 1px solid transparent !important;
  border-radius: 9px !important;

  outline: none !important;

  font-family: inherit !important;
  font-size: 14px !important;
  line-height: 1.5 !important;

  box-shadow: none !important;

  transition:
    border-color .2s ease,
    box-shadow .2s ease,
    background-color .2s ease !important;
}

#contact-form .demirsan-contact-phone-input:focus {
  background: #ffffff !important;

  border-color: rgba(226, 6, 20, .5) !important;

  box-shadow:
    0 0 0 4px rgba(226, 6, 20, .08) !important;
}


/* Bayrak alanı */

#contact-form .demirsan-contact-phone-button {
  width: 48px !important;

  background: transparent !important;

  border: none !important;
  border-right: 1px solid #e3e5e8 !important;

  border-radius: 9px 0 0 9px !important;
}

#contact-form .demirsan-contact-phone-button:hover,
#contact-form .demirsan-contact-phone-button.open {
  background: #eeeeef !important;
}


/* Ülke dropdown */

#contact-form .demirsan-contact-phone-dropdown {
  width: 330px !important;
  max-height: 280px !important;

  margin-top: 6px !important;
  padding: 6px 0 !important;

  background: #ffffff !important;

  border: 1px solid #e1e4e8 !important;
  border-radius: 9px !important;

  box-shadow:
    0 12px 35px rgba(0, 0, 0, .14) !important;

  z-index: 999999 !important;
}


/* Ülke satırları */

#contact-form
.demirsan-contact-phone-dropdown
.country {
  min-height: 42px !important;

  padding-top: 8px !important;
  padding-bottom: 8px !important;

  color: #17213a !important;

  font-size: 13px !important;
}

#contact-form
.demirsan-contact-phone-dropdown
.country:hover {
  background: #f6f7f9 !important;
}

#contact-form
.demirsan-contact-phone-dropdown
.country.highlight {
  background: #fff1f2 !important;
}

#contact-form
.demirsan-contact-phone-dropdown
.dial-code {
  color: #8b93a5 !important;
}


/* Ülke arama */

#contact-form
.demirsan-contact-phone-dropdown
.search {
  position: sticky !important;
  top: 0 !important;

  padding: 8px !important;

  background: #ffffff !important;

  z-index: 2 !important;
}

#contact-form
.demirsan-contact-phone-dropdown
.search-box {
  width: 100% !important;
  height: 40px !important;

  margin: 0 !important;
  padding: 8px 12px !important;

  background: #f6f7f9 !important;

  border: 1px solid #e1e4e8 !important;
  border-radius: 7px !important;

  font-size: 13px !important;
}
  .demirsan-recaptcha-wrapper {
  width: 100%;
  margin: 22px 0 24px;
}

.demirsan-recaptcha-refresh {
  display: inline-flex;
  align-items: center;

  width: auto !important;
  height: auto !important;

  margin-top: 10px !important;
  padding: 0 !important;

  color: #df0a0a !important;
  background: transparent !important;

  border: none !important;
  box-shadow: none !important;

  font-family: inherit;
  font-size: 14px;
  font-weight: 600;

  cursor: pointer;
}

.demirsan-recaptcha-refresh:hover {
  color: #0b1735 !important;
  text-decoration: underline;
}

.demirsan-recaptcha-refresh:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

@media (max-width: 400px) {
  .demirsan-recaptcha-wrapper > div:first-child {
    transform: scale(0.88);
    transform-origin: left top;
  }
}

/* Onay kutusu */
.demirsan-contact-consent {
  display: flex !important;
  align-items: flex-start !important;
  gap: 11px;
  margin: 0 0 22px;
}

.demirsan-contact-consent-checkbox {
  position: static !important;
  display: block !important;
  flex: 0 0 18px !important;
  width: 18px !important;
  min-width: 18px !important;
  max-width: 18px !important;
  height: 18px !important;
  min-height: 18px !important;
  max-height: 18px !important;
  margin: 3px 0 0 !important;
  padding: 0 !important;
  opacity: 1 !important;
  visibility: visible !important;
  border: initial !important;
  border-radius: 3px;
  background: initial !important;
  box-shadow: none !important;
  transform: none !important;
  appearance: auto !important;
  -webkit-appearance: checkbox !important;
  accent-color: #e60000;
  cursor: pointer;
}

.demirsan-contact-consent-content {
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0;
  color: #5c6678;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.6;
}

.demirsan-contact-consent-content label {
  display: inline !important;
  margin: 0 !important;
  padding: 0 !important;
  color: #5c6678 !important;
  font-size: 13px !important;
  font-weight: 400 !important;
  line-height: 1.6 !important;
  cursor: pointer;
}

.demirsan-contact-contract-link {
  display: inline !important;
  width: auto !important;
  height: auto !important;
  margin: 0 3px !important;
  padding: 0 !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  color: #e60000 !important;
  font-family: inherit !important;
  font-size: inherit !important;
  font-weight: 600 !important;
  line-height: inherit !important;
  text-decoration: underline;
  text-underline-offset: 2px;
  vertical-align: baseline;
  cursor: pointer;
}

.demirsan-contact-contract-link:hover {
  color: #0b1735 !important;
  background: transparent !important;
}

/* Sözleşme modalı */
.demirsan-contact-contract-overlay {
  position: fixed;
  inset: 0;
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(7, 16, 36, 0.72);
  backdrop-filter: blur(4px);
}

.demirsan-contact-contract-modal {
  position: relative;
  display: flex;
  flex-direction: column;
  width: min(900px, 100%);
  max-height: calc(100vh - 48px);
  overflow: hidden;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.28);
}

.demirsan-contact-contract-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid #eceef2;
  background: #ffffff;
}

.demirsan-contact-contract-label {
  display: block;
  margin-bottom: 4px;
  color: #e60000;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
}

.demirsan-contact-contract-title {
  margin: 0;
  color: #0b1735;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.35;
}

.demirsan-contact-contract-close {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #f4f5f7;
  color: #5d6677;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  transition: background-color .2s ease, color .2s ease;
}

.demirsan-contact-contract-close:hover {
  background: #e60000;
  color: #ffffff;
}

.demirsan-contact-contract-body {
  flex: 1;
  padding: 18px 20px;
  overflow-y: auto;
  color: #4f5b6e;
  font-size: 13px;
  line-height: 1.7;
}

.demirsan-contact-contract-content {
  color: #4f5b6e;
  font-size: 13px;
  line-height: 1.7;
}

.demirsan-contact-contract-content p { margin: 0 0 14px; }

.demirsan-contact-contract-content h1,
.demirsan-contact-contract-content h2,
.demirsan-contact-contract-content h3,
.demirsan-contact-contract-content h4,
.demirsan-contact-contract-content h5,
.demirsan-contact-contract-content h6 {
  margin-top: 16px !important;
  margin-bottom: 8px !important;
  color: #0b1735 !important;
  font-weight: 700 !important;
  line-height: 1.4 !important;
  background: none !important;
}

body .demirsan-contact-contract-modal .demirsan-contact-contract-content h1 {
  font-size: 20px !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
}

body .demirsan-contact-contract-modal .demirsan-contact-contract-content h2 {
  font-size: 18px !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
}

body .demirsan-contact-contract-modal .demirsan-contact-contract-content h3,
body .demirsan-contact-contract-modal .demirsan-contact-contract-content h4 {
  font-size: 16px !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
}

body .demirsan-contact-contract-modal .demirsan-contact-contract-content h5,
body .demirsan-contact-contract-modal .demirsan-contact-contract-content h6 {
  font-size: 14px !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
}

body .demirsan-contact-contract-modal .demirsan-contact-contract-content h1::before,
body .demirsan-contact-contract-modal .demirsan-contact-contract-content h1::after,
body .demirsan-contact-contract-modal .demirsan-contact-contract-content h2::before,
body .demirsan-contact-contract-modal .demirsan-contact-contract-content h2::after,
body .demirsan-contact-contract-modal .demirsan-contact-contract-content h3::before,
body .demirsan-contact-contract-modal .demirsan-contact-contract-content h3::after {
  content: none !important;
  display: none !important;
  background: none !important;
}

.demirsan-contact-contract-content ul,
.demirsan-contact-contract-content ol { padding-left: 22px; }

.demirsan-contact-contract-content a { color: #e60000; }

.demirsan-contact-contract-loading,
.demirsan-contact-contract-error,
.demirsan-contact-contract-empty {
  padding: 45px 15px;
  text-align: center;
  font-size: 14px;
}

.demirsan-contact-contract-loading { color: #657083; }
.demirsan-contact-contract-error { color: #d40000; }
.demirsan-contact-contract-empty { color: #7a8494; }

.demirsan-contact-contract-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 20px;
  border-top: 1px solid #eceef2;
  background: #fafafa;
}

.demirsan-contact-contract-footer-button {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: auto !important;
  min-width: 90px !important;
  padding: 9px 18px !important;
  border: 0;
  border-radius: 6px;
  background: #0b1735;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.demirsan-contact-contract-footer-button:hover { background: #e60000; }

@media (max-width: 767px) {
  .demirsan-contact-contract-overlay { padding: 10px; }
  .demirsan-contact-contract-modal {
    max-height: calc(100vh - 20px);
    border-radius: 10px;
  }
  .demirsan-contact-contract-header { padding: 17px; }
  .demirsan-contact-contract-title { font-size: 18px; }
  .demirsan-contact-contract-body { padding: 18px; font-size: 13px; }
  .demirsan-contact-contract-footer { padding: 13px 17px; }
}
        `}
      </style>

      <HeaderOne />

      <Breadcrumb title="İletişim" breadcrumbs={breadcrumbs} />

      {/*
       * İletişim kartları ve haritalar.
       * Veriler ContactUs tarafından çekilip
       * MapOne bileşenine gönderiliyor.
       */}
      <MapOne contacts={contacts} loading={contactsLoading} />

      {/* İLETİŞİM FORMU */}
      <div className="rts-contact-form-area">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="rts-contact-fluid rts-section-gap">
                <div className="rts-title-area contact-fluid text-center mb--50 demirsan-clean-title">
                  <p className="pre-title">İLETİŞİM FORMU</p>

                  <h2 className="title">Bizimle İletişime Geçin</h2>
                </div>

                <div className="form-wrapper">
                  <div id="form-messages" aria-live="polite">
                    {formStatus.message && (
                      <div
                        className={`demirsan-contact-form-status ${formStatus.type}`}
                        role={formStatus.type === "error" ? "alert" : "status"}
                      >
                        {formStatus.message}
                      </div>
                    )}
                  </div>
                  <form id="contact-form" onSubmit={handleSubmit}>
                    <div className="name-email">
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Adınız Soyadınız *"
                        autoComplete="name"
                        inputMode="text"
                        pattern="[A-Za-zÇĞİÖŞÜçğıöşü' -]+"
                        title="Ad soyad alanında yalnızca harf kullanabilirsiniz."
                        required
                        disabled={formLoading}
                      />

                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="E-posta Adresiniz"
                        autoComplete="email"
                        inputMode="email"
                        pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                        title="Geçerli bir e-posta adresi yazınız. E-posta adresinde @ karakteri bulunmalıdır."
                        required
                        disabled={formLoading}
                      />
                    </div>

                    <div className="name-email">
                      <div className="demirsan-contact-phone">
                        <PhoneInput
                          country="tr"
                          value={form.phone}
                          onChange={handlePhoneChange}
                          enableSearch={true}
                          disableSearchIcon={true}
                          countryCodeEditable={false}
                          preferredCountries={[
                            "tr",
                            "de",
                            "gb",
                            "us",
                            "fr",
                            "nl",
                            "it",
                            "es",
                          ]}
                          searchPlaceholder="Ülke ara..."
                          searchNotFound="Ülke bulunamadı"
                          inputProps={{
                            name: "phone",
                            required: true,
                            autoComplete: "tel",
                            "aria-label": "Telefon numarası",
                          }}
                          disabled={formLoading}
                          containerClass="demirsan-contact-phone-container"
                          inputClass="demirsan-contact-phone-input"
                          buttonClass="demirsan-contact-phone-button"
                          dropdownClass="demirsan-contact-phone-dropdown"
                          searchClass="demirsan-contact-phone-search"
                        />
                      </div>

                      <input
                        type="text"
                        name="subject"
                        value={form.subject}
                        onChange={handleChange}
                        placeholder="Konu"
                        required
                        disabled={formLoading}
                      />
                    </div>

                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Mesajınızı Yazınız"
                      rows={6}
                      required
                      disabled={formLoading}
                    />

                    <div className="demirsan-contact-consent">
                      <input
                        id="contact-consent"
                        className="demirsan-contact-consent-checkbox"
                        type="checkbox"
                        name="consent"
                        value="true"
                        required
                        checked={isConsentChecked}
                        disabled={formLoading}
                        onChange={(event) => {
                          setIsConsentChecked(event.target.checked);
                          if (event.target.checked && formStatus.type === "error") {
                            setFormStatus({ type: "", message: "" });
                          }
                        }}
                      />
                      <div className="demirsan-contact-consent-content">
                        <label htmlFor="contact-consent">
                          Paylaştığım kişisel verilerin işlenmesini kabul ediyorum.{" "}
                        </label>
                        <button
                          type="button"
                          className="demirsan-contact-contract-link"
                          onClick={openContractModal}
                        >
                          Aydınlatma Metni
                        </button>
                        <span>'ni okudum.</span>
                      </div>
                    </div>

                    <div className="demirsan-recaptcha-wrapper">
                      {recaptchaSiteKey ? (
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          sitekey={recaptchaSiteKey}
                          onChange={handleCaptchaChange}
                          onExpired={handleCaptchaExpired}
                          onErrored={handleCaptchaError}
                          hl="tr"
                        />
                      ) : (
                        <div className="demirsan-contact-form-status error">
                          CAPTCHA yapılandırması bulunamadı.
                        </div>
                      )}
                    </div>

                    <button
                      type="submit"
                      className="rts-btn btn-primary"
                      disabled={formLoading || !recaptchaToken || !isConsentChecked}
                    >
                      {formLoading ? "Gönderiliyor..." : "Mesaj Gönder"}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {contractModalOpen && (
        <div
          className="demirsan-contact-contract-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeContractModal();
            }
          }}
        >
          <div
            className="demirsan-contact-contract-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="contact-contract-modal-title"
          >
            <div className="demirsan-contact-contract-header">
              <div>
                <span className="demirsan-contact-contract-label">
                  Kişisel Verilerin Korunması
                </span>
                <h3
                  id="contact-contract-modal-title"
                  className="demirsan-contact-contract-title"
                >
                  {contractData.title || "Aydınlatma Metni"}
                </h3>
              </div>
              <button
                type="button"
                className="demirsan-contact-contract-close"
                onClick={closeContractModal}
                aria-label="Kapat"
              >
                ×
              </button>
            </div>
            <div className="demirsan-contact-contract-body">
              {contractLoading ? (
                <div className="demirsan-contact-contract-loading">
                  Aydınlatma metni yükleniyor...
                </div>
              ) : contractError ? (
                <div className="demirsan-contact-contract-error">{contractError}</div>
              ) : contractData.editor ? (
                <div
                  className="demirsan-contact-contract-content"
                  dangerouslySetInnerHTML={{ __html: contractData.editor }}
                />
              ) : (
                <div className="demirsan-contact-contract-empty">
                  Aydınlatma metni bulunamadı.
                </div>
              )}
            </div>
            <div className="demirsan-contact-contract-footer">
              <button
                type="button"
                className="demirsan-contact-contract-footer-button"
                onClick={closeContractModal}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      <FooterOne />
    </div>
  );
}

export default ContactUs;
